// Kunden-Sicht: Produktkatalog mit ausklappbaren Gruppen-Akkordeons.
// Jede Produktgruppe wird gefaltet angezeigt — der Kunde öffnet nur die
// Gruppe(n), die ihn interessieren. Innerhalb der Gruppe sind Produktkarten
// mit ausklappbaren technischen Details.
import { useEffect, useState, useCallback } from 'react';
import { authFetch } from '../../lib/auth-fetch';
import { Store, ChevronDown, ChevronUp, Plus, Check, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const fmtEur = (v) => new Intl.NumberFormat('de-DE', {
  style: 'currency', currency: 'EUR', minimumFractionDigits: 2,
}).format(Number(v || 0));

function ProductCard({ product, onAdd, busy }) {
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);

  const handleAdd = async () => {
    const ok = await onAdd(product, Number(qty) || 1);
    if (ok) {
      setAdded(true);
      setTimeout(() => setAdded(false), 1800);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden hover:border-[#9ec043]/40 transition-colors flex flex-col" data-testid={`product-card-${product.articleId}`}>
      {/* Body: zwei Spalten, links Titel/Artikelnummer, rechts Preis (fix oben rechts).
          Min-Height sorgt für konsistente Karten-Höhe unabhängig von Titel-Länge. */}
      <div className="p-3 grid grid-cols-[1fr_auto] gap-3 items-start flex-1">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wide text-gray-400 font-mono mb-0.5">{product.articleNumber || '–'}</div>
          <h4 className="text-sm font-semibold text-[#32373c] leading-tight">{product.title}</h4>
        </div>
        <div className="text-right shrink-0">
          <div className="text-base font-semibold text-[#7ca735] tabular-nums whitespace-nowrap">{fmtEur(product.single_price_net)}</div>
          <div className="text-[10px] text-gray-400 whitespace-nowrap">netto / {product.unit}</div>
        </div>
      </div>
      {/* Footer-Aktionsleiste: immer unten, immer gleiche Position. */}
      <div className="px-3 pb-3 flex items-center justify-end gap-1.5">
        <input
          type="number"
          min="1"
          value={qty}
          onChange={e => setQty(e.target.value)}
          className="w-16 px-2 py-1 border border-gray-200 rounded text-xs tabular-nums focus:outline-none focus:ring-1 focus:ring-[#9ec043]"
          data-testid={`input-qty-${product.articleId}`}
        />
        <button
          onClick={handleAdd}
          disabled={busy}
          className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
            added
              ? 'bg-emerald-500 text-white'
              : 'bg-[#9ec043] text-white hover:bg-[#7ca735] disabled:opacity-50'
          }`}
          data-testid={`btn-add-${product.articleId}`}
        >
          {added ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
          {added ? 'Hinzugefügt' : 'Zur Bestellung'}
        </button>
      </div>
      {product.description && product.description.length > 0 && (
        <>
          <button
            onClick={() => setOpen(!open)}
            className="w-full px-3 py-1.5 text-[11px] text-gray-500 hover:bg-gray-50 border-t border-gray-100 flex items-center justify-between"
            data-testid={`btn-toggle-${product.articleId}`}
          >
            <span>Technische Details {open ? 'ausblenden' : 'anzeigen'}</span>
            {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {open && (
            <pre className="px-3 py-2 text-[11px] text-gray-600 bg-gray-50 whitespace-pre-wrap font-sans leading-relaxed" data-testid={`details-${product.articleId}`}>
              {product.description}
            </pre>
          )}
        </>
      )}
    </div>
  );
}

function GroupAccordion({ group, defaultOpen, onAdd, busy }) {
  const [open, setOpen] = useState(defaultOpen);
  const slug = group.groupName.replace(/\s/g, '-');
  return (
    <section className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid={`group-${slug}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
        data-testid={`group-toggle-${slug}`}
      >
        <div className="flex items-baseline gap-2">
          <h2 className="text-base font-semibold text-[#32373c]">{group.groupName}</h2>
          <span className="text-xs text-gray-400">({group.products.length} Artikel)</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && (
        <div className="px-4 pb-4 grid grid-cols-1 lg:grid-cols-2 gap-2 border-t border-gray-100 pt-3">
          {group.products.map(p => (
            <ProductCard key={p.articleId} product={p} onAdd={onAdd} busy={busy} />
          ))}
        </div>
      )}
    </section>
  );
}

export default function ProductCatalogPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    const r = await authFetch('/api/shop/products');
    if (r.ok) setData(await r.json());
    setLoading(false);
  }, []);

  const loadCart = useCallback(async () => {
    const r = await authFetch('/api/shop/orders/draft');
    if (r.ok) {
      const d = await r.json();
      setCartCount((d.items || []).reduce((s, it) => s + (Number(it.quantity) || 0), 0));
    }
  }, []);

  useEffect(() => { load(); loadCart(); }, [load, loadCart]);

  const addToCart = async (product, quantity) => {
    setBusy(true);
    const r = await authFetch('/api/shop/orders/draft/items', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        articleId: product.articleId,
        articleNumber: product.articleNumber,
        title: product.title,
        description: product.description,
        groupName: product.groupName,
        quantity,
        single_price_net: product.single_price_net,
        vat_percent: product.vat_percent,
        unit: product.unit,
      }),
    });
    setBusy(false);
    if (r.ok) {
      const d = await r.json();
      setCartCount((d.items || []).reduce((s, it) => s + (Number(it.quantity) || 0), 0));
      return true;
    } else {
      const d = await r.json().catch(() => ({}));
      toast.error(d.detail || 'Konnte nicht zur Bestellung hinzufügen');
      return false;
    }
  };

  return (
    <div className="p-6 max-w-6xl" data-testid="product-catalog-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Store className="w-6 h-6 text-[#9ec043]" />
          <h1 className="text-2xl font-semibold text-[#32373c]">Produktkatalog</h1>
        </div>
        <button
          onClick={() => navigate('/dashboard/meine-bestellungen')}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#9ec043] text-white text-xs font-medium hover:bg-[#7ca735]"
          data-testid="btn-go-to-cart"
        >
          <Send className="w-3.5 h-3.5" />
          Bestellung prüfen
          {cartCount > 0 && (
            <span className="bg-white/20 px-1.5 py-0.5 rounded text-[10px] tabular-nums">
              {cartCount}
            </span>
          )}
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-gray-400">Lade Produktkatalog …</div>
      ) : !data || data.groups.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-6 text-sm text-gray-500" data-testid="empty-catalog">
          {data?.message || 'Es sind noch keine Produkte für dich freigegeben. Bitte wende dich an EES Innendienst.'}
        </div>
      ) : (
        <div className="space-y-3">
          {data.groups.map((grp) => (
            <GroupAccordion
              key={grp.groupName}
              group={grp}
              defaultOpen={false}
              onAdd={addToCart}
              busy={busy}
            />
          ))}
        </div>
      )}
    </div>
  );
}
